<template>
  <div class="chart-scroll-wrapper">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(ChartDataLabels);

export default {
  name: 'PieChart',
  props: {
    chartData: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const chartCanvas = ref(null);
    let chartInstance = null;

    const createChart = () => {
      if (chartInstance) {
        chartInstance.destroy();
      }

      if (chartCanvas.value && props.chartData) {
        const ctx = chartCanvas.value.getContext('2d');

        chartInstance = new Chart(ctx, {
          type: 'pie',
          data: props.chartData,
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                rtl: true,
                labels: {
                  font: {
                    family: 'Vazir, Arial, sans-serif'
                  },
                  color: '#fff'
                }
              },
              tooltip: {
                rtl: true,
                titleFont: {
                  family: 'Vazir, Arial, sans-serif'
                },
                bodyFont: {
                  family: 'Vazir, Arial, sans-serif'
                },
                callbacks: {
                  label: function(context) {
                    let label = context.label || '';
                    if (label) {
                      label += ': ';
                    }

                    const value = context.parsed || 0;
                    const data = context.chart.data.datasets[0].data || [];
                    const total = data.reduce((sum, val) => sum + val, 0);
                    const percent = total ? ((value / total) * 100).toFixed(1) : 0;

                    label += `${new Intl.NumberFormat('fa-IR').format(value)} (${percent}٪)`;
                    return label;
                  }
                }
              },
              datalabels: {
                color: '#fff',
                // Force the label to the center of the arc
                anchor: 'center',
                align: 'center',
                // Ensure the label is centered within its own coordinate space
                textAlign: 'center',
                // Some versions require this to prevent "floating" towards the chart center
                offset: 0,
                font: {
                  family: 'Vazir, Arial, sans-serif',
                  weight: 'bold',
                  size: 14 // Increased slightly for visibility
                },
                formatter: (value, context) => {
                  const data = context.chart.data.datasets[0].data || [];
                  const total = data.reduce((sum, val) => sum + val, 0);
                  if (!total) return '';

                  const percentage = (value / total) * 100;

                  // Adjusted threshold: if it's too small, labels will overlap in the middle
                  if (percentage < 10) return ''; 

                  // Formatting the number to Persian digits
                  const formattedPercent = percentage.toFixed(1).toLocaleString('fa-IR');
                  return `${formattedPercent}٪`;
                }
              }
            },
            ...props.options
          }
        });
      }
    };

    onMounted(() => {
      createChart();
    });

    onUnmounted(() => {
      if (chartInstance) {
        chartInstance.destroy();
      }
    });

    watch(
      () => props.chartData,
      () => {
        createChart();
      },
      { deep: true }
    );

    return {
      chartCanvas
    };
  }
};
</script>

<style scoped>
.chart-scroll-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
}

</style>